import {Component} from 'react';
import CheckListSection from './CheckListSection.js';
import CheckListError from './CheckListError.js';
import KeywordDensity from './KeywordDensity.js';
import KeywordList from './KeywordList.js';
import './CheckList.scss';
import checks from '../checkConfig.js';
import SeoHelper from '../lib/SeoHelper.js';
import Tooltip from './Tooltip.js';
import InternalLinkSuggestions from './InternalLinkSuggestions.js';
import ArticleGenerator from './ArticleGenerator.js';
import IconChevron from './IconChevron.js';
import languages from '../languages.js';
import WorkIndicator from './WorkIndicator.js';
import LocalStorage from '../lib/LocalStorage.js';
import TextUtils from '../lib/TextUtils.js';
import Markup from '../lib/Markup.js';
import Fetch from '../lib/Fetch.js';
import IconPadlock from './IconPadlock.js';
import Description from './Description.js';
import IconInfo from './IconInfo.js';

class CheckList extends Component {
	constructor(props) {
		super(props);

		this.state = {
			keyword: LocalStorage.getItem('content-focus-keyword') || '',
			relatedKeywords: LocalStorage.getItem('content-related-keywords') || [],
			isKeywordMissing: false,
			searchResults: LocalStorage.getItem('content-search-results') || {},
			searchResultMarkup: [],
			optimalValues: LocalStorage.getItem('content-optimal-values') || {},
			siteUrl: '',
			selectedCheck: '',
			openSections: ['focus-keyword'],
			loading: false,
			loadingTitle: '',
			loadingTitleInfo: '',
			loadingInfo: '',
			loadingDetails: '',
			loadingProgress: 0,
			loaderType: '',
		}

		this.handleKeywordChange = this.handleKeywordChange.bind(this);
		this.handleCheckReport = this.handleCheckReport.bind(this);
		this.handleUrlChange = this.handleUrlChange.bind(this);
		this.setRelatedKeywords = this.setRelatedKeywords.bind(this);
		this.selectCheck = this.selectCheck.bind(this);
		this.getOptimalData = this.getOptimalData.bind(this);
		this.handleSectionToggleClick = this.handleSectionToggleClick.bind(this);
		this.checkKeyword = this.checkKeyword.bind(this);
		this.getOptimalKeywordCoverage = this.getOptimalKeywordCoverage.bind(this);
		this.getTop10SearchResults = this.getTop10SearchResults.bind(this);
		this.handleOptimalDataButtonClick = this.handleOptimalDataButtonClick.bind(this);
		this.updateState = this.updateState.bind(this);
		this.loadKeywordsAndOptimalValues = this.loadKeywordsAndOptimalValues.bind(this);
		this.handleDescrLinkClick = this.handleDescrLinkClick.bind(this);
		this.toggleDescription = this.toggleDescription.bind(this);
	}

	componentDidMount() {
		const openSection = window.location.hash.substring(1);
		if (openSection) {
			let openSections = this.state.openSections;
			openSections.push(openSection);

			this.setState({
				openSections: openSections,
			});
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevState.relatedKeywords !== this.state.relatedKeywords) {
			this.setState({
				optimalValues: this.calculateOptimalValues(this.state.keyword, this.state.searchResults, this.state.relatedKeywords),
			});
		}
	}

	addCheck(checkData) {
		let index = checks.findIndex((check) => check.id === checkData.id);
		if ( index === -1 ) {
			checks.push(checkData);
		}
	}

	removeCheck(id) {
		let index = checks.findIndex((check) => check.id === id);
		if (index !== -1) {
			checks.splice(index, 1);
			this.handleCheckReport(id, 'unmount');
		}
	}

	handleCheckReport(checkId, what) {
		let index = checks.findIndex((check) => check.id === checkId);
		if (index !== -1) {
			switch (what) {
				case 'mount':
					checks[index].mounted = true;
					break;
				case 'pass':
					checks[index].passed = true;
					break;
				case 'fail':
					checks[index].passed = false;
					break;
				default:
			}
		}

		let score = this.calculateScore();
		this.props.scoreCallback(score);
	}

	handleDescrLinkClick(event) {
		event.preventDefault();
		this.toggleDescription();

	}

	toggleDescription() {
		this.setState({
			showingDescription: ! this.state.showingDescription,
		});
	}

	selectCheck(id) {
		this.setState({
			selectedCheck: id
		});
	}

	calculateScore() {
		let mountedChecks = checks.filter((check) => check.mounted);
		let passedChecks = checks.filter((check) => check.passed && check.mounted);

		let score = mountedChecks.length ? Math.round(passedChecks.length / mountedChecks.length * 100) : 0;
		return score;
	}

	filterChecks(sectionName) {
		const sectionChecks = checks.filter((check) => check.section === sectionName);
		return sectionChecks;
	}

	handleKeywordChange(event) {
		LocalStorage.setItem('content-focus-keyword', event.target.value);
		this.setState({
			keyword: event.target.value,
		});
	}

	setRelatedKeywords(items) {
		let id = 'related-keywords-in-text';

		if (items.length) {
			this.addCheck({id: id, mounted: true});
			let allWordsInText = true;
			items.forEach((keyword) => {
				let found = TextUtils.countOccurrences(keyword.name, this.props.content);
				if (!found) {
					allWordsInText = false;
				}
			});
			this.handleCheckReport(id, allWordsInText ? 'pass' : 'fail');
		} else {
			this.removeCheck(id);
		}

		LocalStorage.setItem('content-related-keywords', items);
		this.setState({
			relatedKeywords: items,
		});
	}

	updateState(newState) {
		this.setState(newState);
	}

	handleUrlChange(newUrl) {
		this.setState({
			siteUrl: newUrl || '',
		});
		if (newUrl) {
			LocalStorage.setItem('content-page-url', newUrl);
		} else {
			LocalStorage.removeItem('content-page-url');
		}
	}

	handleSectionToggleClick(event) {
		event.preventDefault();
		let openSections = this.state.openSections;
		let currentSection = event.target.dataset.section;

		if (openSections.includes(currentSection)) {
			openSections = openSections.filter((section) => currentSection !== section);
		} else {
			openSections.push(currentSection);
		}
		this.setState({
			openSections: openSections,
		});
	}

	handleOptimalDataButtonClick(event) {
		event.preventDefault();
		this.getOptimalData(this.state.searchResults);
	}

	render() {
		const content = {
			content: this.props.content,
			keyword: this.state.keyword || this.props.keyword,
			siteUrl: this.state.siteUrl || LocalStorage.getItem('content-page-url') || '',
			optimal: LocalStorage.getItem('content-optimal-values') || this.state.optimalValues,
		};

		const fieldCallbacks = {
			domainCallback: this.handleUrlChange,
		}

		const selectedCheck = checks.find(check => check.id === this.state.selectedCheck);

		const workIndicator = this.state.loading
			? <WorkIndicator
				title={this.state.loadingTitle}
				titleInfo={this.state.loadingTitleInfo}
				info={this.state.loadingInfo}
				details={this.state.loadingDetails}
				loaderType={this.state.loaderType}
				progress={this.state.loadingProgress}
				loadingData={this.state.loadingData}
			/>
			: '';

		const descriptionPopup = this.state.showingDescription
			? <Description closeCallback={this.toggleDescription} />
			: '';

		var keywordFieldClass = '';
		var keywordError = '';
		if (this.state.isKeywordMissing) {
			keywordFieldClass = 'field-error';
			keywordError = (<CheckListError text={window?.t?.ERR_NO_FOCUS_KEYWORD} />);
		}

		return (
			<>
				{workIndicator}
				{descriptionPopup}
				<div className="check-list">
					<div className={this.state.openSections.includes('focus-keyword') ? 'check-list-section open' : 'check-list-section'} id="keyword-placement">
						<div className="check-list-section-content">
							<label htmlFor="keyword">{window?.t?.LBL_FOCUS_KEYWORD}</label>
							<input type="text" id="keyword" onChange={this.handleKeywordChange} value={content.keyword} className={keywordFieldClass} />
							{keywordError}
						</div>
					</div>
					<div className={this.state.openSections.includes('keyword-placement') ? 'check-list-section open' : 'check-list-section'} id="keyword-placement">
						<h2 className="check-list-section-title">
							<a href="/" onClick={this.handleSectionToggleClick} data-section="keyword-placement">{window?.t?.KEYWORD_PLACEMENT}</a>
							<IconChevron />
						</h2>
						<div className="check-list-section-content">
							<KeywordDensity pageContent={content} appSettings={this.props.appSettings} />
							<KeywordList
								pageContent={content}
								language={this.props.language}
								location={this.props.appSettings.location}
								relatedKeywords={this.state.relatedKeywords}
								reportCallback={this.handleCheckReport}
								getOptimalValues={this.loadKeywordsAndOptimalValues}
								saveRelatedKeywords={this.setRelatedKeywords}
								toggleSettingsCallback={this.props.toggleSettingsCallback}
								optimalData={this.state.optimalValues}
								limitUsage={this.props.limitUsage}
								limitUsageCallback={this.props.limitUsageCallback}
								optimalDataHandler={this.handleOptimalDataButtonClick}
							/>

							<CheckListSection
								checks={this.filterChecks('keyword')}
								content={content}
								contentCallback={this.props.contentCallback}
								reportCallback={this.handleCheckReport}
								fieldCallbacks={fieldCallbacks}
								isPro={this.props.isPro}
								appSettings={this.props.appSettings}
							/>
						</div>
					</div>
					<div className={this.state.openSections.includes('content-checks') ? 'check-list-section open' : 'check-list-section'} id="content-checks">
						<h2 className="check-list-section-title">
							<a href="/" onClick={this.handleSectionToggleClick} data-section="content-checks">{window?.t?.CONTENT_CHECKPOINTS}</a>
							<IconChevron />
						</h2>
						<div className="check-list-section-content">
							<CheckListSection
								checks={this.filterChecks('content')}
								content={content}
								contentCallback={this.props.contentCallback}
								reportCallback={this.handleCheckReport}
								fieldCallbacks={fieldCallbacks}
								isPro={this.props.isPro}
								appSettings={this.props.appSettings}
								selectCheckCallback={this.selectCheck}
								selectedCheck={this.state.selectedCheck}
							/>
						</div>
					</div>
					<div className={this.state.openSections.includes('ai-article-generator') ? 'check-list-section open' : 'check-list-section'} id="ai-article-generator">
						<h2 className="check-list-section-title">
							<a href="/" onClick={this.handleSectionToggleClick} data-section="ai-article-generator">{window?.t?.AI_ARTICLE_GENERATOR}</a>
							<IconChevron />
						</h2>
						<div className="check-list-section-content">
							<ArticleGenerator
								isPro={this.props.isPro}
								appSettings={this.props.appSettings}
								language={this.props.language}
								contentCallback={this.props.contentCallback}
								focusKeyword={this.state.keyword}
								relatedKeywords={this.state.relatedKeywords}
								getOptimalData={this.getOptimalData}
								optimalValues={this.state.optimalValues}
								top10SearchResults={this.state.searchResults}
								limits={this.props.limits}
								limitUsage={this.props.limitUsage}
								limitUsageCallback={this.props.limitUsageCallback}
								checkKeyword={this.checkKeyword}
								permissions={this.props.permissions}
								toggleSettingsCallback={this.props.toggleSettingsCallback}
								passInfo={this.updateState}
								loadKeywordsAndOptimalValues={this.loadKeywordsAndOptimalValues}
								currentArticle={this.props.content}
							/>
						</div>
					</div>
					<div className={this.state.openSections.includes('internal-links') ? 'check-list-section internal-links open' : 'check-list-section internal-links'} id="internal-links">
						<h2 className="check-list-section-title">
							<a href="/" onClick={this.handleSectionToggleClick} data-section="internal-links">{window?.t?.AI_INTERNAL_LINKS}</a>
							<IconChevron />
						</h2>
						<div className="check-list-section-content">
							{this.props.permissions.includes('content-internal-links') ? (
								<>
								<CheckListSection
									checks={this.filterChecks('internal-links')}
									content={content}
									contentCallback={this.props.contentCallback}
									reportCallback={this.handleCheckReport}
									fieldCallbacks={fieldCallbacks}
									isPro={this.props.isPro}
									appSettings={this.props.appSettings}
									selectCheckCallback={this.selectCheck}
									selectedCheck={this.state.selectedCheck}
								/>
								<InternalLinkSuggestions
									content={content.content}
									siteUrl={content.siteUrl}
									focusKeyword={content.keyword}
									contentCallback={this.props.contentCallback}
									isPro={this.props.isPro}
									appSettings={this.props.appSettings}
									toggleSettingsCallback={this.props.toggleSettingsCallback}
									limitUsage={this.props.limitUsage}
									limitUsageCallback={this.props.limitUsageCallback}
								/>
								</>
							) : (<p><IconPadlock/> {window?.t?.AVAILABLE_IN_ADVANCED}</p>)}

						</div>
					</div>
					<div className="check-list-section open">
						<div className="check-list-section-content">
							<p className="description-link"><IconInfo /><a href="#tool-description" onClick={this.handleDescrLinkClick}>{window?.t?.DESCRIPTION_LINK_TEXT}</a></p>
						</div>
					</div>
				</div>
				<Tooltip check={selectedCheck} />
			</>
		);
	}

	getCurrentLanguageName() {
		let {language} = this.props;
		let i = languages.findIndex((item) => item.langCode === language);
		let languageName = i > -1 ? languages[i].name : '';
		return languageName;
	}

	getCurrentLanguageNameTranslation() {
			let i = languages.findIndex((item) => item.langCode === this.props.language);
			let t = i > -1 ? languages[i].t : '';
			return window?.t[`LANG_${t}`];
		}

	/**
	 * Loads related keywords and optimal data
	 */
	async loadKeywordsAndOptimalValues() {
		const focusKeyword = this.state.keyword.trim();
		let searchResults = this.state.searchResults;
		if (!focusKeyword) {
			this.setState({
				loading: false,
				error: window?.t?.ERR_NO_FOCUS_KEYWORD,
			})
			return;
		}

		this.setState({
			loading: true,
			loadingTitle: window?.t?.WAIT,
			loadingTitleInfo: window?.t?.DONT_CLOSE_WINDOW,
			loadingInfo: `${window?.t?.LOADING_TOP10_FOR} "${focusKeyword}"`,
			loadingData: {
				focusKeyword: focusKeyword,
				language: this.getCurrentLanguageNameTranslation(),
			},
			error: '',
		});

		if (!searchResults.urls || searchResults.keyword !== focusKeyword || searchResults.language !== this.getCurrentLanguageName()) {
			let newSearchResults = await this.getTop10SearchResults();

			if (!newSearchResults.urls) {
				this.setState({
					loading: false,
					loadingTitle: '',
					loadingTitleInfo: '',
					loadingInfo: '',
					error: window?.t?.ERR_LOADING_RELATED_KEYWORDS,
				});
				return;
			} else {
				searchResults = newSearchResults;
			}
		}

		this.setState({
			loadingInfo: `${window?.t?.LOADING_RELATED_KEYWORDS} "${focusKeyword}"`,
		});
		let relatedKeywords = this.state.relatedKeywords.length ? this.state.relatedKeywords : [];
		const {appSettings} = this.props;

		if (!relatedKeywords.length) {
			switch (appSettings.keywordSrc) {
				case 'tfidf':
					relatedKeywords = searchResults && searchResults.urls ? await this.loadKeywordsFromAPI(searchResults.urls) : [];
					break;
				case 'ai':
					relatedKeywords = focusKeyword ? await this.loadKeywordsFromAI(focusKeyword, this.state.language) : [];
					break;
				default:
					relatedKeywords = searchResults.urls && searchResults.markup && focusKeyword ? await this.loadKeywordsFromSERP(searchResults.urls, searchResults.markup, focusKeyword, this.state.language.name) : [];
			}
		}

		const optimalValues = this.calculateOptimalValues(focusKeyword, searchResults, relatedKeywords);

		this.setState({
			loading: false,
			loadingTitle: '',
			loadingTitleInfo: '',
			loadingInfo: '',
			error: '',
			searchResults: searchResults || {},
			optimalValues: optimalValues || {},
			relatedKeywords: relatedKeywords.length ? relatedKeywords : [],
		});
		LocalStorage.setItem('content-related-keywords', relatedKeywords);
		LocalStorage.setItem('content-optimal-values', optimalValues);
		if (searchResults) {
			LocalStorage.setItem('content-search-results', searchResults);
		}
		return {
			searchResults: searchResults || {},
			optimalValues: optimalValues || {},
			relatedKeywords: relatedKeywords.length ? relatedKeywords : [],
		}
	}

	async getTop10SearchResults() {
		const keyword = this.state.keyword || LocalStorage.getItem('content-focus-keyword');
		const location = this.props.appSettings.location;
		const searchResults = this.state.searchResults;

		if (!keyword) {
			return false;
		}
		if (keyword === searchResults.keyword && this.getCurrentLanguageName() === searchResults.language) {
			return searchResults;
		}



		const results = await SeoHelper.getTop10SearchResults(keyword, this.getCurrentLanguageName(), location);
		if (!results.length) {
			return false;
		}

		const markups = [];
		for (let i = 0; i < results.length; i ++) {
			this.setState({
				loadingDetails: `${window?.t?.LOADING_DATA_FROM_PAGE} ${i + 1} ${window?.t?.OF} ${results.length}`,
			});
			let markup = await SeoHelper.getPageMarkup(results[i]);
			if (markup.markup) {
				markups.push(Markup.cleanResourceLinks(markup.markup));
			}
			this.setState({
				loadingDetails: '',
			});
		}
		const newSearchResults = {
			keyword: keyword,
			language: this.getCurrentLanguageName(),
			urls: results,
			markup: markups.filter((item) => item !== undefined),
		};

		return newSearchResults;
	}

	calculateOptimalValues(keyword, searchResults, relatedKeywords) {
		const focusKeyword = keyword;
		relatedKeywords = relatedKeywords.length ? relatedKeywords.map((keyword) => keyword.name) : [];

		const pageData = {
			wordsInP: [],
			imageCount: [],
			headingCount: [],
			keywordDensity: [],
			relatedKeywordCoverage: [],
		};

		const pageMarkup = searchResults.markup;
		if (pageMarkup && pageMarkup.length) {
			pageMarkup.forEach((html, i) => {
				let lowerHtml = html.toLowerCase();
				let images = (lowerHtml.match(/<img/g) || []).length;
				pageData.imageCount.push(images);

				html = Markup.cleanContent(html);
				let markup = Markup.createMarkupEl(html);

				let headings = markup.querySelectorAll('h2, h3, h4, h5, h6');
				pageData.headingCount.push(headings.length || 0);

				let paragraphs = markup.querySelectorAll('p, h2, h3, h4, h5, h6');
				let wordCount = 0;
				paragraphs.forEach((p) => {
					let text = p.textContent;
					wordCount += TextUtils.countWords(text);
				});
				pageData.wordsInP.push(wordCount);

				let keywordDensity = 0;
				let text = Markup.stripTags(html).toLocaleLowerCase();
				if (focusKeyword) {
					let words = text.split(' ');
					words = words.filter((el) => {
						return el !== '';
					});
					let keywordCount = TextUtils.countOccurrences(focusKeyword, text);

					if (keywordCount) {
						keywordDensity = keywordCount / words.length * 100;
						keywordDensity = parseFloat(keywordDensity.toFixed(2));
					}
				}
				pageData.keywordDensity.push(keywordDensity);

				if (relatedKeywords) {
					let foundKeywords = 0;
					relatedKeywords.forEach((keyword) => {
						if (TextUtils.isPhraseInText(keyword, text)) {
							foundKeywords ++;
						}
					});

					let keywordCoverage = foundKeywords / relatedKeywords.length * 100;
					pageData.relatedKeywordCoverage.push(keywordCoverage);
				} else {
					pageData.relatedKeywordCoverage.push(0);
				}
			});
		} else {
			pageData.relatedKeywordCoverage.push(0);
		}


		const optimalValues = {
			optimalHeadingCount: SeoHelper.avg(pageData.headingCount),
			optimalImageCount: SeoHelper.avg(pageData.imageCount),
			optimalWordCount: SeoHelper.avg(pageData.wordsInP),
			optimalKeywordDensity: SeoHelper.avg(pageData.keywordDensity),
			optimalKeywordCoverage: SeoHelper.avg(pageData.relatedKeywordCoverage),
			keyword: focusKeyword,
			language: this.props.language,
		};
		if (optimalValues.optimalWordCount > 5000) {
			optimalValues.optimalWordCount = Math.round(5000 / 1.3);
		}
		if (optimalValues.optimalHeadingCount > 30) {
			optimalValues.optimalHeadingCount = 30;
		}

		return optimalValues;
	}
	checkKeyword() {
		this.setState({
			isKeywordMissing: !this.state.keyword
		});
	}

	async getOptimalKeywordCoverage() {
		let {searchResults, optimalValues} = this.state;
		const relatedKeywords = this.state.relatedKeywords.map((keyword) => keyword.name);

		if (!searchResults.markup.length) {
			return;
		}

		const coverage = [];
		searchResults.markup.forEach((html) => {
			let text = Markup.stripTags(html).toLocaleLowerCase();
			if (relatedKeywords.length) {
				let foundKeywords = 0;
				relatedKeywords.forEach((keyword) => {
					if (TextUtils.isPhraseInText(keyword, text)) {
						foundKeywords ++;
					}
				});
				let keywordCoverage = foundKeywords / relatedKeywords.length * 100;
				coverage.push(keywordCoverage);
			} else {
				coverage.push(0);
			}
		});

		optimalValues.optimalKeywordCoverage = SeoHelper.avg(coverage);
		this.setState({
			optimalValues: optimalValues,
		});
	}

	async getOptimalData() {
		if (!this.state.keyword) {
			return;
		}
		if (this.state.working) {
			let originalPageTitle = document.title.replaceAll(`${window?.t?.LOADING_OPTIMAL_SEO_DATA} `, '');
			document.title = `${window?.t?.LOADING_OPTIMAL_SEO_DATA} ${originalPageTitle}`;
		} else {
			let newTitle = document.title.replaceAll(`${window?.t?.LOADING_OPTIMAL_SEO_DATA} `, '');
			document.title = newTitle;
		}

		let optimalValues = LocalStorage.getItem('content-optimal-values') || {};

		if (optimalValues.keyword === this.state.keyword) {
			alert(`${window?.t?.SEO_DATA_LOADED1} "${this.state.keyword}" ${window?.t?.SEO_DATA_LOADED2}`);
			this.setState({
				optimalValues: optimalValues,
			});
			return optimalValues;
		}

		this.setState({
			loading: true,
			loadingTitle: window?.t?.WAIT,
			loadingTitleInfo: window?.t?.DONT_CLOSE_WINDOW,
			loadingInfo: window?.t?.LOADING_OPTIMAL_SEO_DATA,
		});

		let searchResults = this.state.searchResults;
		if (!searchResults || searchResults.keyword !== this.state.keyword) {
			searchResults = await this.getTop10SearchResults();
		}

		optimalValues = this.calculateOptimalValues(this.state.keyword, searchResults, this.state.relatedKeywords);
		this.setState({
			optimalValues: optimalValues || {},
			loading: false,
			loadingTitle: '',
			loadingTitleInfo: '',
			loadingInfo: '',
		});

		LocalStorage.setItem('content-optimal-values', optimalValues);
		return optimalValues;
	}

	async loadKeywordsFromAI(focusKeyword, language) {
		const result = await Fetch.post('/tools/api/get-related-keywords-ai.php', {
			'focus-keyword': focusKeyword,
			language: language.name,
			count: 50,
		});

		let newKeywords = [];
		if (result.keywords) {
			let responseText = result.keywords.replaceAll("\n", '');
			let words = responseText.split(',');
			words = words.map((word) => {
				return word.trim().replace(/\.$/, '');
			});
			words = words.filter((word) => !!word);
			newKeywords = words.map((word) => {
				return {
					name: word,
					repeat_min: null,
					repeat_max: null,
				}
			})
		}
		let keywords = this.props.relatedKeywords || [];
		if (newKeywords.length) {
			newKeywords.forEach((newKeyword) => {
				if (!keywords.length || !keywords.find((word) => word.name === newKeyword.name)) {
					keywords.push(newKeyword);
				}
			});
		}
		return newKeywords;
	}

	async loadKeywordsFromAPI(urls) {

		const tfidfData = await Fetch.post('/tools/api/get-related-keywords-tfidf.php', {
			competitorUrls: urls,
			tags_check: 1,
			skip_full: 1,
			websiteWords: [],
			free: 0,
		});

		let newKeywords = tfidfData && tfidfData.status === 'ok' ? tfidfData.data.splice(0, 50) : [];

		const keywords = this.props.relatedKeywords || [];
		if (newKeywords.length) {
			newKeywords.forEach((newKeyword) => {
				if (!keywords.find((word) => word.name === newKeyword.word)) {
					newKeyword.name = newKeyword.word;
					delete(newKeyword.word);
					keywords.push(newKeyword);
				}
			});
		}
		return keywords;
	}

	async loadKeywordsFromSERP(top10urls, top10markups, focusKeyword, language) {
		let relatedKeywords = this.props.relatedKeywords || [];
		const serpItems = [];

		for (let i = 0; i < top10urls.length; i ++) {
			const markup = Markup.cleanResourceLinks(top10markups[i]);
			const html = Markup.createMarkupEl(markup);
			const titleEl = html.querySelector('title');
			const title = titleEl ? titleEl.textContent : '';
			const descrEl = html.querySelector('meta[name="description"]');
			const descr = descrEl ? descrEl.getAttribute('content') : '';
			html.innerHTML = Markup.cleanContent(html.innerHTML);
			const headings = html.querySelectorAll('h1, h2, h3, h4, h5, h6');
			const headingContent = [];
			if (headings.length) {
				headings.forEach((heading) => headingContent.push(heading.textContent));
			}

			const contentEl = html.querySelectorAll('p, ul, ol, blockquote');
			const content = [];
			if (contentEl.length) {
				contentEl.forEach((el) => content.push(el.textContent.replace(/[\n]/gi, ' ').replace(/[\t]/gi, '')));
			}

			serpItems.push({
				url: top10urls[i],
				markup: markup,
				title: title,
				description: descr,
				headingContent: headingContent.join("\n"),
				content: content.join(' '),
			});
		}
		if (!serpItems.length) {
			return [];
		}

		const sources = serpItems.map((page) => {
			return {
				url: page.url,
				title: page.title,
				description: page.description,
				headings: page.headingContent,
				content: page.content,
			}
		});

		let newKeywords = [];
		const srcTypes = ['title', 'description', 'headings', 'content'];
		for (let i = 0; i < srcTypes.length; i ++) {
			let type = srcTypes[i];
			let src = sources.map((item) => item[type] || '');
			if (!src.join('').trim()) {
				continue;
			}

			let entityResult = await Fetch.post('/tools/content/ajax/get-keyword-entities.php', {
				sources: src,
				focusKeyword: focusKeyword,
				language: language,
				srcType: type,
			});
			if (entityResult.status === 'ok') {
				newKeywords = newKeywords.concat(entityResult.entities);
			}
		}

		// making sure each keyword is unique
		newKeywords = newKeywords.filter((item, index) => {
			let i = newKeywords.findIndex((word) => word.name === item.name);
			return i === index;
		});

		// filtering keywords we already have
		newKeywords = newKeywords.filter((newKeyword) => relatedKeywords.findIndex((keyword) => keyword.name === newKeyword.name) === -1);

		// getting search volume for the new keywords
		let found = 0;
		let phrases = newKeywords.map((keyword) => keyword.name);
		let entitySearchVolume = await SeoHelper.fetchSearchVolume(phrases, this.state.language.langCode);
		if (entitySearchVolume.status === 'ok') {
			entitySearchVolume.data.forEach((item) => {
				let index = newKeywords.findIndex((word) => word.name === item.keyword);
				if (index !== -1) {
					found ++;
					newKeywords[index].searchVolume = item.searchVolume;
				}
			});
		}

		// calculating keyword density and occurence count for each keyword
		newKeywords.forEach((keyword) => {
			if (!relatedKeywords.find((word) => word.name === keyword.name)) {
				let densities = [];
				keyword.count = 0;
				sources.forEach((source) => {
					let text = [source.title, source.description, source.headings, source.content].join(' ');

					let density = 0;
					if (text && keyword.name) {
						let keywordCount = TextUtils.countOccurrences(keyword.name, text);
						keyword.count += keywordCount;
						let wordCount = text.split(' ').length;
						if (keywordCount) {
							density = keywordCount / wordCount * 100;
						}
					}
					densities.push(density);
				});
				densities = densities.filter((d) => ! isNaN(d));
				keyword.density = densities.length ? densities.reduce((a, b) => a + b, 0) / densities.length : 0;
			}
		});

		// sorting by occurence count, by searchVolume if same occurence count
		newKeywords.sort((a, b) => {
			if (a.count > b.count) {
				return -1;
			}
			if (a.count < b.count) {
				return 1;
			}
			if (a.searchVolume > b.searchVolume) {
				return -1;
			}
			if (a.searchVolume < b.searchVolume) {
				return 1;
			}
			return 0;
		});

		// adding 50 new keywords to already existing related keywords
		let bestNewKeywords = newKeywords.splice(0, 50);
		let newRelatedKeywords = relatedKeywords.concat(bestNewKeywords);
		return newRelatedKeywords;
	}

	async getCompetitorWords(competitorUrls) {
		const keywords = await Fetch.post('/tools/tf-idf/ajax/create_competitors_arr_str.php', {
			competitors_url: competitorUrls,
			tags_check: 1,
			skip_full: 1,
		});
		return keywords;
	}

	async getTfIdf(competitorWords) {

		const data = await Fetch.post('/tools/tf-idf/ajax/compute_tf_idf.php', {
			competitorWords: competitorWords,
			websiteWords: [],
			wordCount: 0,
			free: 0,
		});
		return data.table || [];
	}
}

export default CheckList;